import type { PagedResultDto } from '@abp/ng.core';
import { RestService } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import { LookupDto, LookupRequestDto } from '@proxy/shared/models';
import {
  GetPublishedModelsInput,
  ModuleResourceDto,
  ModuleVersionWithNavigationDto,
  PublishedModelCreateDto,
  PublishedModelDto,
  PublishedModelImportDto,
  PublishedModelUpdateDto,
  PublishedModelWithNavigationPropertiesDto,
} from './published-model.model';

@Injectable({
  providedIn: 'root',
})
export class PublishedModelService {
  apiName = 'Default';

  create = (input: PublishedModelCreateDto) =>
    this.restService.request<any, PublishedModelDto>(
      {
        method: 'POST',
        url: `/api/app/published-models`,
        body: input,
      },
      { apiName: this.apiName }
    );

  delete = (id: string) =>
    this.restService.request<any, void>(
      {
        method: 'DELETE',
        url: `/api/app/published-models/${id}`,
      },
      { apiName: this.apiName }
    );

  get = (id: string) =>
    this.restService.request<any, PublishedModelDto>(
      {
        method: 'GET',
        url: `/api/app/published-models/${id}`,
      },
      { apiName: this.apiName }
    );

  getList = (input: GetPublishedModelsInput) =>
    this.restService.request<any, PagedResultDto<PublishedModelWithNavigationPropertiesDto>>(
      {
        method: 'GET',
        url: `/api/app/published-models`,
        params: {
          filterText: input.filterText,
          name: input.name,
          description: input.description,
          sorting: input.sorting,
          skipCount: input.skipCount,
          maxResultCount: input.maxResultCount,
        },
      },
      { apiName: this.apiName }
    );

  getExperimentSnapshotLookup = (input: LookupRequestDto) =>
    this.restService.request<any, PagedResultDto<LookupDto<string>>>(
      {
        method: 'GET',
        url: `/api/app/published-models/experiment-snapshot-lookup`,
        params: {
          filter: input.filter,
          skipCount: input.skipCount,
          maxResultCount: input.maxResultCount,
        },
      },
      { apiName: this.apiName }
    );

  getModuleVersionLookup = (input: LookupRequestDto) =>
    this.restService.request<any, PagedResultDto<LookupDto<string>>>(
      {
        method: 'GET',
        url: `/api/app/published-models/module-version-lookup`,
        params: {
          filter: input.filter,
          skipCount: input.skipCount,
          maxResultCount: input.maxResultCount,
        },
      },
      { apiName: this.apiName }
    );

  getWithNavigationProperties = (id: string) =>
    this.restService.request<any, PublishedModelWithNavigationPropertiesDto>(
      {
        method: 'GET',
        url: `/api/app/published-models/with-navigation-properties/${id}`,
      },
      { apiName: this.apiName }
    );

  update = (id: string, input: PublishedModelUpdateDto) =>
    this.restService.request<any, PublishedModelDto>(
      {
        method: 'PUT',
        url: `/api/app/published-models/${id}`,
        body: input,
      },
      { apiName: this.apiName }
    );

  getPublicEndpointList = () =>
    this.restService.request<any, any>(
      {
        method: 'GET',
        url: `/api/app/public-endpoints`,
        params: { maxResultCount: 200 },
      },
      { apiName: this.apiName }
    );

  getOutsourcedModules = () =>
    this.restService.request<void, ModuleVersionWithNavigationDto[]>(
      {
        method: 'GET',
        url: `/api/app/modules/outsourced-modules`,
      },
      { apiName: this.apiName }
    );

  getModuleResources = (moduleVersionId: string) =>
    this.restService.request<void, ModuleResourceDto[]>(
      {
        method: 'GET',
        url: `/api/app/module-resources/outsourced-module-resources/${moduleVersionId}`,
      },
      { apiName: this.apiName }
    );

  getDependencyModuleResources = () =>
    this.restService.request<void, Map<string, ModuleResourceDto[]>>(
      {
        method: 'GET',
        url: `/api/app/module-resources/dependency-module-resources`,
      },
      { apiName: this.apiName }
    );

  importModule = (input: PublishedModelImportDto) =>
    this.restService.request<PublishedModelImportDto, PublishedModelDto>(
      {
        method: 'POST',
        url: `/api/app/published-models/import`,
        body: input,
      },
      { apiName: this.apiName }
    );

  constructor(private restService: RestService) {}
}
